import { NO_MODULE } from '@platform/schema/authoring';

import type { BrowseLessonsResponse } from '@platform/client';

/**
 * The Course, Module and Lesson filters, and how picking one moves the others — ADMIN-18.
 *
 * A pick fills what sits above it, because a lesson has one module and a module one
 * course. Changing a parent clears whatever it no longer holds.
 */
type Lesson = BrowseLessonsResponse['lessons'][number];

export type Place = NonNullable<Lesson['module']>;

export interface ModulePlace extends Place {
  course?: Place;
}

export interface LessonPlace extends Place {
  module?: ModulePlace;
}

/** Offered first in the Module filter, for the lessons still waiting to be placed. */
export const UNPLACED: ModulePlace = { id: NO_MODULE, title: 'No module' };

export interface Places {
  course: Place | null;
  module: ModulePlace | null;
  lesson: LessonPlace | null;
}

export const NO_PLACES: Places = { course: null, module: null, lesson: null };

export const lessonPlace = (row: Lesson): LessonPlace => ({
  id: row.id,
  title: row.title,
  ...(row.module === undefined
    ? {}
    : {
        module: {
          ...row.module,
          ...(row.course === undefined ? {} : { course: row.course }),
        },
      }),
});

export function pickCourse(places: Places, course: Place | null): Places {
  if (course === null) return { ...places, course };

  return {
    course,
    module: places.module?.course?.id === course.id ? places.module : null,
    lesson:
      places.lesson?.module?.course?.id === course.id ? places.lesson : null,
  };
}

export function pickModule(places: Places, module: ModulePlace | null): Places {
  if (module === null) return { ...places, module };

  const lessonModule = places.lesson?.module?.id ?? NO_MODULE;

  return {
    course: module.course ?? null,
    module,
    lesson: lessonModule === module.id ? places.lesson : null,
  };
}

export function pickLesson(places: Places, lesson: LessonPlace | null): Places {
  if (lesson === null) return { ...places, lesson };

  return {
    course: lesson.module?.course ?? null,
    module: lesson.module ?? UNPLACED,
    lesson,
  };
}

export const placeQuery = ({ course, module, lesson }: Places) => ({
  ...(course === null ? {} : { courseId: course.id }),
  ...(module === null ? {} : { moduleId: module.id }),
  ...(lesson === null ? {} : { lessonId: lesson.id }),
});
